import { useEffect, useState, useRef, useCallback } from 'react'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { onDatadogRumReady } from '@/lib/analytics/datadogRum'

interface UseLaunchDarklyFlagOptions {
    /**
     * If true, the flag will re-evaluate when the user's identity changes (e.g., after sign-up).
     * This ensures the frontend flag value stays in sync with the backend.
     * Defaults to false to preserve existing behavior for other flags.
     */
    reevaluateOnAuthChange?: boolean
}

export function useLaunchDarklyFlag(flagName: string, options?: UseLaunchDarklyFlagOptions) {
    const { reevaluateOnAuthChange = false } = options || {}

    const [flagEnabled, setFlagEnabled] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isInitialized, setIsInitialized] = useState<boolean>(false)
    // The key LaunchDarkly bucketed this evaluation on. Exposed so callers can
    // attribute experiment assignment to the same identity LD used.
    const [contextKey, setContextKey] = useState<string | null>(null)
    const ldClient = useLDClient()
    const ldClientRef = useRef(ldClient)
    const isEvaluatingRef = useRef<boolean>(false)
    const pendingReEvaluationRef = useRef<boolean>(false)

    // Keep ldClient ref in sync
    useEffect(() => {
        ldClientRef.current = ldClient
        if (!ldClient) {
            setFlagEnabled(false)
            setIsLoading(false)
            setIsInitialized(false)
        }
    }, [ldClient])

    const evaluateFlag = useCallback(async () => {
        // Prevent concurrent evaluations - if already evaluating, mark as pending and return
        if (isEvaluatingRef.current) {
            pendingReEvaluationRef.current = true
            return
        }
        isEvaluatingRef.current = true

        // Helper function to set failure state
        const setFailureState = () => {
            setFlagEnabled(false)
            setIsLoading(false)
            setIsInitialized(false)
        }

        try {
            const client = ldClientRef.current
            if (!client) {
                setFailureState()
                return
            }

            setIsLoading(true)

            // Wait for LaunchDarkly client to be ready before evaluating flags
            // This ensures we get the correct flag value and prevents race conditions
            // Timeout of 5 seconds to prevent hanging indefinitely
            try {
                await client.waitForInitialization(5)
                setIsInitialized(true)
            } catch (err) {
                // If waitForInitialization fails or times out, we cannot safely evaluate flags
                // Set initialized to false and stop evaluation to prevent incorrect flag values
                console.error('LaunchDarkly waitForInitialization failed, cannot evaluate flags:', err)
                setFailureState()
                return
            }
            // Don't call identify() - LaunchDarklyProvider already handles identification via context prop
            // Calling identify() manually causes reconnections and infinite loops
            // The provider's context prop is the source of truth for user identification

            // Direct flag evaluation
            const value = await client.variation(flagName, false)
            setFlagEnabled(value === true)

            const evaluationContext = client.getContext?.()
            setContextKey(
                evaluationContext && 'key' in evaluationContext ? (evaluationContext.key as string) : null,
            )

            // Report flag evaluation to Datadog RUM so sessions/errors can
            // be filtered and correlated by which feature flags were active.
            onDatadogRumReady((datadogRum) => {
                datadogRum.addFeatureFlagEvaluation(flagName, value)
            })
        } catch (err) {
            console.error('Error evaluating flag:', err)
            setFlagEnabled(false)
        } finally {
            setIsLoading(false)
            isEvaluatingRef.current = false

            // Check if a re-evaluation was requested while we were evaluating
            if (pendingReEvaluationRef.current) {
                pendingReEvaluationRef.current = false
                // Use setTimeout to avoid potential stack overflow and allow state to settle
                setTimeout(() => evaluateFlag(), 0)
            }
        }
    }, [flagName])

    // Evaluate flag when dependencies change
    useEffect(() => {
        if (ldClient) {
            evaluateFlag()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ldClient, flagName]) // Only depend on ldClient and flagName, not evaluateFlag

    // Re-evaluate when user identity changes (e.g., after sign-up)
    // The ldClient reference doesn't change when context changes, so we need to listen
    // for the patientIdChanged event to trigger re-evaluation
    // Only enabled when reevaluateOnAuthChange is true
    useEffect(() => {
        if (!reevaluateOnAuthChange) return
        if (typeof window === 'undefined') return

        const handlePatientIdChange = () => {
            // Call evaluateFlag - it will queue a re-evaluation if one is already in progress
            evaluateFlag()
        }

        window.addEventListener('patientIdChanged', handlePatientIdChange)

        return () => {
            window.removeEventListener('patientIdChanged', handlePatientIdChange)
        }
    }, [evaluateFlag, reevaluateOnAuthChange])

    // Also listen for LaunchDarkly flag changes (fires after identify or flag updates)
    // Only enabled when reevaluateOnAuthChange is true
    useEffect(() => {
        if (!reevaluateOnAuthChange) return
        if (!ldClient) return

        const handleChange = (changes: Record<string, { current: unknown; previous: unknown }>) => {
            // Only update if our specific flag changed
            if (flagName in changes) {
                const newValue = changes[flagName].current
                setFlagEnabled(newValue === true)

                // Report updated flag value to Datadog RUM
                onDatadogRumReady((datadogRum) => {
                    datadogRum.addFeatureFlagEvaluation(flagName, newValue)
                })
            }
        }

        ldClient.on('change', handleChange)

        return () => {
            ldClient.off('change', handleChange)
        }
    }, [ldClient, flagName, reevaluateOnAuthChange])

    return { flagEnabled, isLoading, isInitialized, contextKey }
}
